import { api, handleError, scenarioApi } from '@ardoq/api';
import {
  type APIScenarioScopeData,
  type APIScopeData,
  type ArdoqId,
  PersonalSetting,
} from '@ardoq/api-types';
import { logError } from '@ardoq/logging';
import { alert } from '@ardoq/modal';
import * as profiling from '@ardoq/profiling';
import {
  diffSourceToTarget,
  enhanceScopeData,
  enhanceScopeDataWithBranchName,
  getDynamicFilter,
  getWorkspacesFromMainlineAndBranch,
  ignoredProperties,
} from '@ardoq/renderers';
import {
  apply,
  collectRoutines,
  dispatchAction,
  extractPayload,
  ofType,
  routine,
} from '@ardoq/rxbeach';
import {
  notifyScenarioChanged,
  resetState,
} from 'streams/context/ContextActions';
import { addBreadcrumb } from '@ardoq/sentry';
import { dispatchActionAndWaitForResponse } from 'actions/utils';
import { hideRightPane, requestShowAppModule } from 'appContainer/actions';
import { hideScopeRelatedNavigator } from 'appContainer/MainAppModule/MainAppModuleSidebar/actions';
import { closeVisualDiff } from 'appContainer/MainAppModule/MainAppModuleSidebar/NavigatorTopBar/utils';
import { isRightEditorPaneOpen$ } from 'appLayout/isRightEditorPaneOpen$';
import { trackClickOpenVisualDiff } from 'scope/tracking';
import { hasReadAccessToAllWorkspacesInScenario } from 'scope/utils';
import { focusedChromeOperations } from 'scope/focusedChromeOperations';
import { closePresentation } from 'presentation/presentations2024/actions';
import { featureContext$ } from 'streams/featureContext$';
import { selectedSlide$ } from 'streams/selectedSlide/selectedSlide$';
import { setActiveMainTabLeft } from 'streams/views/mainContent/actions';
import { AppModules } from 'appContainer/types';
import { setScenarioRelatedComponentIds } from 'streams/components/ComponentActions';
import { globalFilters as Filters } from 'collections/filters';
import { setGroupingRules } from 'streams/grouping/GroupingActions';
import { groupingRuleInterface } from 'modelInterface/groupingRules/groupingRuleInterface';
import { resetPerspectives } from 'collections/helpers/loadPerspective';
import { workspaceInterface } from '@ardoq/workspace-interface';
import { Branch } from 'components/DiffMergeTable/Branch';
import { clearLoadedState } from 'loadedState/actions';
import { scenarioInterface } from 'modelInterface/scenarios/scenarioInterface';
import { pipe } from 'rxjs';
import {
  catchError,
  filter,
  map,
  switchMap,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import {
  getEntities,
  getOnlyScenarioRelatedIds,
  getScopeSize,
  openScenarioIsOutOfSyncModal,
  openWelcomeScenarioModal,
  updateSyncStatusIfChanged,
} from 'scope/utils';
import { dataInterface } from '@ardoq/data-interface';
import {
  loadAndFixDiffContext,
  loadAndFixScenario,
} from 'services/scenarioApi';
import {
  activeScenario$,
  getActiveScenarioId,
} from 'streams/activeScenario/activeScenario$';
import { triggerFiltersChangedEvent } from 'streams/filters/FilterActions';
import { setScenarioRelatedReferenceIds } from 'streams/references/ReferenceActions';
import { modelNamespace } from 'streams/models/actions';
import { workspaceNamespace } from 'streams/workspaces/actions';
import { workspaceOverviewNamespace } from 'streams/workspaces/workspaceOverviews$';
import {
  fetchAllRequested,
  fetchAllSucceeded,
  fetchAllFailed,
} from 'streams/crud/actions';
import { clearLoadedGraphState } from 'streams/traversals/actions';
import {
  resolveComponentStyleSheetEntryArgs,
  toComponentStyleSheetEntry,
  toModelStyleSheetEntry,
  toModelStyleSheetEntryArgs,
} from 'utils/modelCssManager/ardoqModelCSSManager';
import { scenarioIsInSync$ } from '../scenarioIsInSync$';
import { setTagsFromScenario } from '../tabview/tagscape/actions';
import {
  closeLoadingScenarioProgressBar,
  closeScenario,
  closeScenarioSuccess,
  loadDiff,
  loadDiffError,
  loadDiffSuccess,
  openScenario,
  openVisualDiff,
  openScenarioError,
  openScenarioSuccess,
  reloadScenario,
  reloadScenarioError,
  reloadScenarioSuccess,
  setLoadedScenarioData,
  setScenarioId,
  setScenarioIsInSync,
  updateEnhancedScopeDiffData,
  updateLoadedScenarioData,
  updateScopeDiff,
} from './actions';
import { trackOpenScenario } from './tracking';
import { contextInterface } from '../modelInterface/contextInterface';
import {
  closeAllWorkspacesSilently,
  justCloseWorkspaces,
  setComponentId,
  setScenarioId as setScenarioIdContext,
} from '../streams/context/ContextActions';
import { componentInterface } from '@ardoq/component-interface';
import { referenceInterface } from '@ardoq/reference-interface';
import { toIds, toIdSet, userOperations } from '@ardoq/core';
import { currentUserInterface } from 'modelInterface/currentUser/currentUserInterface';
import { logErrorIfInconsistentDataForScopeData } from 'utils/logErrorIfInconsistentData';
import { scopeDataOperations } from '@ardoq/scope-data';
import { addToStyleSheet } from '../utils/modelCssManager/actions';
import { context$ } from 'streams/context/context$';
import { fieldNamespace } from 'streams/fields/actions';
import { uniqBy } from 'lodash';
import { currentUserPermissionContext$ } from 'streams/currentUserPermissions/currentUserPermissionContext$';
import { getLoadedPersonalSetting } from 'streams/currentUser/getLoadedPersonalSetting';
import { removeDanglingReferencesInScopeData } from 'scopeData/utils/fixScopeData';

// TODO temporary solution
/** @deprecated */
const genericScenarioErrorHandler = (
  message: string,
  cb?: (error: any) => void
) =>
  catchError((error, stream) => {
    dispatchAction(closeLoadingScenarioProgressBar());
    api.logErrorIfNeeded(error);
    alert({
      title: 'An error occured',
      text: message,
    });
    cb?.(error);
    return stream;
  });

const openScenarioRoutine = routine(
  ofType(openScenario),
  extractPayload(),
  apply(
    ({
      workspaceId,
      componentId,
      trackingClickSource,
      scenarioId,
      isPresentationSlide,
    }) =>
      pipe(
        withLatestFrom(featureContext$, selectedSlide$),
        tap(([, featureContext, { selectedSlideId }]) => {
          if (
            focusedChromeOperations.shouldClosePresentationOnOpenScenario({
              featureContext,
              isShowingSlide: Boolean(selectedSlideId),
              isPresentationSlide: Boolean(isPresentationSlide),
            })
          ) {
            dispatchAction(closePresentation());
          }
          dispatchAction(resetState({ keepCurrentViewPointMode: false }));
          closeVisualDiff();
          dispatchAction(
            clearLoadedGraphState({ keepCurrentViewPointMode: false })
          );
          dispatchAction(clearLoadedState());
          addBreadcrumb('Opening scenario', { data: { scenarioId } });
        }),
        switchMap(() => scenarioApi.load(scenarioId)),
        handleError(),
        tap(({ inSync }) =>
          dispatchAction(setScenarioIsInSync({ isInSync: inSync }))
        ),
        map(response =>
          removeDanglingReferencesInScopeData(response, scenarioId)
        ),
        filter(response => {
          if (response.workspaces.length === 0) {
            dispatchAction(closeLoadingScenarioProgressBar());
            alert({
              title: 'Empty scenario',
              text: 'Sorry, for now, you are unable to open an empty scenario. Instead, go to the hamburger menu and click update components to add content.',
            });
            return false;
          }
          return true;
        }),
        withLatestFrom(
          currentUserPermissionContext$,
          featureContext$,
          activeScenario$
        ),
        tap(
          ([
            scenarioData,
            permissionContext,
            featureContext,
            activeScenario,
          ]) => {
            checkDataConsistencyAndLogError(scenarioData, scenarioId);
            trackOpenScenario(
              trackingClickSource,
              getScopeSize(scenarioData),
              scenarioId,
              hasReadAccessToAllWorkspacesInScenario(
                scenarioData,
                permissionContext
              )
            );
            logErrorIfInconsistentDataForScopeData({
              components: scenarioData.components,
              scopeData: scenarioData,
              isLogErrors: true,
            });
            const transaction = profiling.startTransaction(
              'open scenario',
              2000,
              profiling.Team.TRANSFORMATION
            );
            handleLoadedScopeData(scenarioId, scenarioData, workspaceId);

            dispatchAction(
              requestShowAppModule({ selectedModule: AppModules.WORKSPACES })
            );
            dispatchAction(closeLoadingScenarioProgressBar());

            /**
             * `withLatestFrom` sampled `activeScenario` before this tap ran, so it
             * still names the scenario being left even though
             * `handleLoadedScopeData` above has set the new id.
             */
            const openViewId = focusedChromeOperations.getScenarioOpenViewId({
              featureContext,
              isPresentationSlide: Boolean(isPresentationSlide),
              isReopeningActiveScenario:
                activeScenario.scenarioId === scenarioId,
            });
            if (openViewId) {
              dispatchAction(
                setActiveMainTabLeft({
                  activeTabId: openViewId,
                  isViewpointMode: false,
                })
              );
            }

            if (componentId && componentInterface.isComponent(componentId)) {
              setTimeout(() => {
                dispatchAction(setComponentId(componentId));
                dispatchAction(openScenarioSuccess());
              });
            } else if (
              workspaceId &&
              workspaceInterface.isWorkspace(workspaceId)
            ) {
              setTimeout(async () => {
                await contextInterface.switchToWorkspace(workspaceId);
                dispatchAction(openScenarioSuccess());
              });
            } else if (scenarioInterface.exists(scenarioId)) {
              setTimeout(() => {
                dispatchAction(setScenarioIdContext(scenarioId));
                dispatchAction(openScenarioSuccess());
              });
            } else {
              dispatchAction(openScenarioSuccess());
            }
            dispatchAction(hideRightPane()); // closing sidebar menu
            // If the scenario is loaded from a presentation slide
            // grouping rules and filters from that slide will be set.
            if (!isPresentationSlide) {
              preserveGroupsAndFormattingFilters();
            }
            profiling.endTransaction(transaction);
          }
        )
      )
  ),
  genericScenarioErrorHandler(
    'Error occured on trying to open a scenario',
    () => dispatchAction(openScenarioError())
  )
);

const preserveGroupsAndFormattingFilters = async () => {
  const formattingFilters = Filters.getFormattingFilters();
  const { groupingRules, transactionId } =
    groupingRuleInterface.getActiveGroupingAttributes();
  resetPerspectives();
  Filters.set(formattingFilters);
  dispatchAction(setGroupingRules({ groupingRules, transactionId }));
  dispatchAction(triggerFiltersChangedEvent());
};

const checkDataConsistencyAndLogError = (
  { components, references }: APIScopeData,
  scenarioId: ArdoqId
) => {
  const componentIds = toIdSet(components);
  const errorMessage = references.reduce(
    (currentMessage, { _id, source, target }) => {
      const missingSourceMessage = componentIds.has(source)
        ? ''
        : `Component ${_id} is missing source: ${source}.`;
      const missingTargetMessage = componentIds.has(target)
        ? ''
        : `Component ${_id} is missing target: ${target}.`;
      const messageToAppend = `${missingSourceMessage}${missingTargetMessage}`;
      return currentMessage + messageToAppend;
    },
    ''
  );
  const hasMissingTargetOrSource = !!errorMessage;
  if (hasMissingTargetOrSource) {
    logError(Error('Missing source or target in scope data'), errorMessage, {
      scenarioId,
    });
  }
};

const reloadScenarioRoutine = routine(
  ofType(reloadScenario),
  extractPayload(),
  tap(({ scenarioId }) =>
    addBreadcrumb('Reloading scenario', { data: { scenarioId } })
  ),
  switchMap(({ scenarioId }) => loadAndFixScenario(scenarioId)),
  handleError(error => {
    api.logErrorIfNeeded(error);
    dispatchAction(reloadScenarioError());
  }),
  withLatestFrom(context$, scenarioIsInSync$),
  tap(([scenarioData, { componentId, workspaceId }, { isInSync }]) => {
    const transaction = profiling.startTransaction(
      'reload scenario',
      2000,
      profiling.Team.TRANSFORMATION
    );

    setCollections(scenarioData, workspaceId, componentId);
    const modelStyleSheets = scenarioData.models
      .map(toModelStyleSheetEntryArgs)
      .map(toModelStyleSheetEntry)
      .filter(([, entries]) => entries.length > 0);

    if (modelStyleSheets.length > 0) {
      dispatchAction(addToStyleSheet(modelStyleSheets));
    }

    const componentStyleSheets = scenarioData.components
      .map(resolveComponentStyleSheetEntryArgs)
      .map(toComponentStyleSheetEntry);

    dispatchAction(addToStyleSheet(componentStyleSheets));

    if (!isInSync) {
      updateSyncStatusIfChanged();
    }

    dispatchAction(setLoadedScenarioData({ scenarioData }));
    dispatchAction(reloadScenarioSuccess());

    profiling.endTransaction(transaction);
  }),
  genericScenarioErrorHandler(
    'Something went wrong adding to the scenario',
    () => dispatchAction(reloadScenarioError())
  )
);

const handleLoadedScopeData = (
  scenarioId: ArdoqId,
  scenarioData: APIScenarioScopeData,
  workspaceId?: ArdoqId
) => {
  dispatchAction(setScenarioId({ scenarioId }));
  dispatchAction(justCloseWorkspaces());
  setCollections(scenarioData, workspaceId);
  dispatchAction(setLoadedScenarioData({ scenarioData }));
};

export const setDefaultCollectionViews = (
  scenarioData: APIScenarioScopeData
) => {
  const { components, references, connected } = scenarioData;

  dispatchAction(
    setScenarioRelatedComponentIds(
      getOnlyScenarioRelatedIds(components, connected.components)
    )
  );

  dispatchAction(
    setScenarioRelatedReferenceIds(
      getOnlyScenarioRelatedIds(references, connected.references)
    )
  );
};

const setCollections = (
  scenarioData: APIScenarioScopeData,
  workspaceId?: ArdoqId,
  componentId?: ArdoqId
) => {
  const {
    workspaces,
    components,
    references,
    connected,
    tags,
    models,
    fields,
  } = scenarioData;

  dataInterface.resetWithScopeData(
    {
      workspaces: [...connected.workspaces, ...workspaces],
      models: [...connected.models, ...models],
      fields: uniqBy([...connected.fields, ...fields], '_id'),
      components: [...connected.components, ...components],
      references: [...connected.references, ...references],
      tags: tags,
    },
    { silent: false, merge: true }
  );

  setDefaultCollectionViews(scenarioData);
  dispatchAction(setTagsFromScenario(tags));
  const workspaceIds = toIds(workspaces);
  const activeWorkspaceId =
    workspaceId && workspaceIds.includes(workspaceId) ? workspaceId : null;

  contextInterface.setOpenWorkspaces({
    workspaceIds,
    activeWorkspaceId,
  });

  if (
    componentId &&
    componentInterface.getWorkspaceId(componentId || '') === activeWorkspaceId
  ) {
    contextInterface.setComponentById(componentId);
  }
};
const hideWelcomeModal = async () =>
  (await getLoadedPersonalSetting(
    PersonalSetting.SCENARIOS_HIDE_WELCOME_MODAL
  )) ?? false;

const onOpenScenarioModalRoutine = routine(
  ofType(openScenarioSuccess),
  withLatestFrom(scenarioIsInSync$),
  tap(async ([, { isInSync }]) => {
    if (!isInSync) {
      const isSyncNow = await openScenarioIsOutOfSyncModal();
      if (isSyncNow) {
        return;
      }
    }
    if (
      !(await hideWelcomeModal()) &&
      !userOperations.isImpersonating(
        currentUserInterface.getCurrentUserAttributes()
      )
    ) {
      await openWelcomeScenarioModal();
    }
  })
);

const closeScenarioRoutine = routine(
  ofType(closeScenario),
  tap(async () => {
    dispatchAction(setScenarioId({ scenarioId: null }));
    dispatchAction(setScenarioIsInSync({ isInSync: false }));
    dispatchAction(updateScopeDiff({ scopeDiff: null }));
    dispatchAction(
      setLoadedScenarioData({
        scenarioData: null,
      })
    );
    dispatchAction(
      updateEnhancedScopeDiffData({
        main: null,
        branch: null,
        branchOff: null,
        diffData: null,
      })
    );

    dispatchAction(justCloseWorkspaces());
    dispatchAction(closeAllWorkspacesSilently());
    resetPerspectives();
    dataInterface.clearCollections();

    const fetchModelsPromise = dispatchActionAndWaitForResponse(
      fetchAllRequested(modelNamespace),
      fetchAllSucceeded,
      fetchAllFailed,
      modelNamespace
    );

    const fetchWorkspacesPromise = dispatchActionAndWaitForResponse(
      fetchAllRequested(workspaceNamespace),
      fetchAllSucceeded,
      fetchAllFailed,
      workspaceNamespace
    );

    const fetchFieldsPromise = dispatchActionAndWaitForResponse(
      fetchAllRequested(fieldNamespace),
      fetchAllSucceeded,
      fetchAllFailed,
      fieldNamespace
    );

    const fetchWorkspaceOverviewsPromise = dispatchActionAndWaitForResponse(
      fetchAllRequested(workspaceOverviewNamespace),
      fetchAllSucceeded,
      fetchAllFailed,
      workspaceOverviewNamespace
    );

    await Promise.all([
      fetchWorkspacesPromise,
      fetchWorkspaceOverviewsPromise,
      fetchModelsPromise,
      fetchFieldsPromise,
    ]);

    dispatchAction(notifyScenarioChanged(null));
    dispatchAction(closeScenarioSuccess());
  }),
  genericScenarioErrorHandler('Error occured on trying to close a scenario')
);

const loadDiffRoutine = routine(
  ofType(loadDiff),
  tap(() => {
    dispatchAction(
      updateLoadedScenarioData({
        components: componentInterface.getAllComponents(),
        references: referenceInterface.getAllReferences(),
      })
    );
    dispatchAction(hideScopeRelatedNavigator());
  }),
  extractPayload(),
  tap(({ scenarioId }) =>
    addBreadcrumb('Loading scenario diff', { data: { scenarioId } })
  ),
  apply(({ scenarioId }) =>
    pipe(
      switchMap(() => loadAndFixDiffContext(scenarioId!)),
      handleError(),
      tap(({ master, branch, branchOff }) => {
        if (scenarioId && scenarioId !== getActiveScenarioId()) {
          handleLoadedScopeData(scenarioId, {
            ...branch,
            connected:
              scopeDataOperations.getEmptyScenarioScopeData().connected, // for type reason, connected data is not returned for diffcontext
          });
        }

        handleDiffContext({
          master,
          branch,
          branchOff,
        });

        dispatchAction(loadDiffSuccess());
      })
    )
  ),
  genericScenarioErrorHandler(
    'Error occured on trying to load the diff to master',
    () => dispatchAction(loadDiffError())
  )
);

export const openVisualDiffRoutine = routine(
  ofType(openVisualDiff),
  extractPayload(),
  withLatestFrom(isRightEditorPaneOpen$, activeScenario$),
  tap(([{ clickSource }, isRightEditorPaneOpen, activeScenario]) => {
    dispatchAction(loadDiff({ scenarioId: activeScenario.scenarioId }));
    if (isRightEditorPaneOpen) {
      dispatchAction(hideRightPane());
    }
    trackClickOpenVisualDiff(clickSource);
  })
);

type HandleDiffContextArguments = {
  master: APIScopeData;
  branch: APIScopeData;
  branchOff: APIScopeData;
};

export const handleDiffContext = ({
  master,
  branch,
  branchOff,
}: HandleDiffContextArguments) => {
  const branchData = enhanceScopeData(branch, {
    shouldFreeze: false,
  });
  const masterData = enhanceScopeData(master, {
    shouldFreeze: false,
  });
  const dynamicFilter = getDynamicFilter(branchData, masterData);

  const { diffData } = diffSourceToTarget({
    sourceData: branch,
    targetData: master,
    dynamicFilter,
    ignoredProperties,
  });

  dispatchAction(
    updateEnhancedScopeDiffData({
      main: enhanceScopeDataWithBranchName(masterData, Branch.MAIN),
      branch: enhanceScopeDataWithBranchName(branchData, Branch.BRANCH),
      branchOff: enhanceScopeDataWithBranchName(
        enhanceScopeData(
          {
            ...branchOff,
            workspaces: getWorkspacesFromMainlineAndBranch(master, branch),
          },
          { shouldFreeze: false }
        ),
        Branch.BRANCH_OFF
      ),
      diffData,
    })
  );

  const {
    branch: branchComponents,
    master: masterComponents,
    diff: diffComponents,
  } = getEntities(branch.components, master.components, diffData.components);

  const {
    branch: branchReferences,
    master: masterReferences,
    diff: diffReferences,
  } = getEntities(branch.references, master.references, diffData.references);

  dispatchAction(
    updateScopeDiff({
      scopeDiff: {
        branchComponents,
        masterComponents,
        diffComponents,
        branchReferences,
        masterReferences,
        diffReferences,
      },
    })
  );
};

export const scopeRoutines = collectRoutines(
  loadDiffRoutine,
  openVisualDiffRoutine,
  reloadScenarioRoutine,
  onOpenScenarioModalRoutine
);

export const scenarioNavigationRoutines = collectRoutines(
  openScenarioRoutine,
  closeScenarioRoutine
);
